import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Calendar, User, Eye, ArrowRight, ExternalLink, Play, Image as ImageIcon, X } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { getImageUrl } from '../lib/utils';

const NewsDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchNews();
  }, [id]);

  const fetchNews = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/news/${id}`);
      const data = await response.json();
      setNews(data);
      setSelectedImage(data.image);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11
      ? `https://www.youtube.com/embed/${match[2]}`
      : null;
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = news.title_ar;
    
    let shareUrl = '';
    
    switch(platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`;
        break;
      case 'instagram':
        if (navigator.clipboard) {
          navigator.clipboard.writeText(url);
          toast.success('تم نسخ رابط الخبر! جاري نقلك إلى Instagram...');
        }
        shareUrl = 'https://www.instagram.com/';
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const openImageModal = (imageUrl) => {
    setModalImage(imageUrl);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setModalImage(null);
  };

  const cleanAndFormatContent = (content) => {
    if (!content) return '';

    // If content does not contain HTML tags, split paragraphs
    if (!/<[a-z][\s\S]*>/i.test(content)) {
      return content
        .split(/\n\n+/)
        .map(p => `<p class="mb-4 leading-relaxed text-lg text-gray-800 dark:text-gray-200">${p.trim()}</p>`)
        .join('');
    }

    // Clean troublesome Word / inline styles (hardcoded fonts, background colors)
    let clean = content
      .replace(/font-family\s*:[^;"]+;?/gi, '')
      .replace(/background-color\s*:[^;"]+;?/gi, '')
      .replace(/style="\s*"/gi, '')
      .replace(/style='\s*'/gi, '')
      .replace(/<p\b([^>]*)>/gi, '<p class="mb-5 leading-relaxed text-lg text-gray-800 dark:text-gray-200" $1>');

    return clean;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F388E]"></div>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">الخبر غير موجود</h2>
          <button
            onClick={() => navigate('/news')}
            className="text-[#0F388E] hover:text-[#C9A040]"
          >
            العودة إلى الأخبار
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b pt-28 relative z-10" style={{ paddingTop: '100px' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link
            to="/news"
            className="flex items-center gap-2 text-[#0F388E] hover:text-[#C9A040] transition-colors inline-flex"
          >
            <ArrowRight className="w-5 h-5" />
            <span>العودة إلى الأخبار</span>
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0F388E] to-[#1a4fa8] text-white py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-4">
            <span className="inline-block bg-[#C9A040] px-4 py-1 rounded-full text-sm font-semibold">
              {news.category}
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
            {news.title_ar}
          </h1>
          
          <div className="flex flex-wrap items-center gap-6 text-white/90">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span>{new Date(news.published_date).toLocaleDateString('ar-EG', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}</span>
            </div>
            <div className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              <span>{news.views || 0} مشاهدة</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Main Image */}
              {selectedImage && (
                <Card className="mb-8 overflow-hidden">
                  <img
                    src={getImageUrl(selectedImage)}
                    alt={news.title_ar}
                    className="w-full h-96 object-cover"
                  />
                </Card>
              )}

              {/* Content */}
              <Card className="mb-8 shadow-sm border border-gray-100 dark:border-gray-800">
                <CardContent className="p-8">
                  <div
                    className="prose prose-lg max-w-none text-gray-800 dark:text-gray-200 leading-relaxed font-normal overflow-hidden"
                    dangerouslySetInnerHTML={{ __html: cleanAndFormatContent(news.content_ar) }}
                  />

                  {news.content_en && (
                    <div className="mt-8 pt-8 border-t border-gray-100 dark:border-gray-700" dir="ltr">
                      <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-4">English Details</h4>
                      <div
                        className="prose prose-lg max-w-none text-gray-700 dark:text-gray-300 leading-relaxed font-normal"
                        dangerouslySetInnerHTML={{ __html: cleanAndFormatContent(news.content_en) }}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Image Gallery */}
              {news.images && news.images.length > 0 && (
                <Card className="mb-8">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <ImageIcon className="w-6 h-6 text-[#0F388E]" />
                      <h2 className="text-2xl font-bold text-[#0F388E]">معرض الصور</h2>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {news.images.map((img, idx) => (
                        <div
                          key={idx}
                          className="relative group cursor-pointer overflow-hidden rounded-lg"
                          onClick={() => openImageModal(img)}
                        >
                          <img
                            src={getImageUrl(img)}
                            alt={`صورة ${idx + 1}`}
                            className="w-full h-48 object-cover transform group-hover:scale-110 transition-transform duration-300"
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="text-white text-sm">عرض بالحجم الكامل</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Video */}
              {news.video_url && (
                <Card className="mb-8">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <Play className="w-6 h-6 text-[#0F388E]" />
                      <h2 className="text-2xl font-bold text-[#0F388E]">الفيديو</h2>
                    </div>
                    {getYouTubeEmbedUrl(news.video_url) ? (
                      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                        <iframe
                          className="absolute top-0 left-0 w-full h-full rounded-lg"
                          src={getYouTubeEmbedUrl(news.video_url)}
                          title="Video"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                    ) : (
                      <video
                        controls
                        className="w-full rounded-lg"
                        src={news.video_url}
                      >
                        متصفحك لا يدعم تشغيل الفيديو
                      </video>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Related Links */}
              {news.links && news.links.length > 0 && (
                <Card className="mb-8 sticky top-4">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-[#0F388E] mb-4">روابط ذات صلة</h3>
                    <div className="space-y-3">
                      {news.links.map((link, idx) => (
                        <a
                          key={idx}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-3 bg-gray-50 hover:bg-[#0F388E]/5 rounded-lg transition-colors group"
                        >
                          <ExternalLink className="w-4 h-4 text-[#C9A040] flex-shrink-0" />
                          <span className="text-gray-700 group-hover:text-[#0F388E] text-sm">
                            {link.title}
                          </span>
                        </a>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Share */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-[#0F388E] mb-4">مشاركة</h3>
                  <div className="space-y-3">
                    <button 
                      onClick={() => handleShare('facebook')}
                      className="w-full bg-[#1877F2] text-white py-2.5 px-4 rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      مشاركة على Facebook
                    </button>
                    <button 
                      onClick={() => handleShare('twitter')}
                      className="w-full bg-black text-white py-2.5 px-4 rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                      مشاركة على X
                    </button>
                    <button 
                      onClick={() => handleShare('whatsapp')}
                      className="w-full bg-[#25D366] text-white py-2.5 px-4 rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                      مشاركة على WhatsApp
                    </button>
                    <button 
                      onClick={() => handleShare('instagram')}
                      className="w-full bg-gradient-to-r from-[#833ab4] via-[#fd1d1d] to-[#fcb045] text-white py-2.5 px-4 rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2 shadow-sm font-medium"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                      مشاركة على Instagram
                    </button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Image Modal */}
      {showImageModal && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={closeImageModal}
        >
          <button
            onClick={closeImageModal}
            className="absolute top-4 right-4 text-white hover:text-[#C9A040] transition-colors z-10"
          >
            <X className="w-8 h-8" />
          </button>
          <div className="max-w-7xl max-h-[90vh] w-full">
            <img
              src={getImageUrl(modalImage)}
              alt="عرض الصورة"
              className="w-full h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsDetail;
